import {useCallback, useEffect, useRef, useState} from 'react'

export interface PrettifiedBlock {
  id: string
  code: string
  collapsed: boolean
}

let nextId = 0
function generateBlockId() {
  return `prettified-block-${++nextId}`
}

export function usePrettifiedBlocks() {
  const [blocks, setBlocks] = useState<PrettifiedBlock[]>([])
  // ref kept in sync so getBlocksText() can read current state without stale closure issues
  const blocksRef = useRef<PrettifiedBlock[]>([])

  useEffect(() => {
    return () => {
      blocksRef.current = []
    }
  }, [])

  const updateBlocks = useCallback((updater: (prev: PrettifiedBlock[]) => PrettifiedBlock[]) => {
    setBlocks(prev => {
      const next = updater(prev)
      blocksRef.current = next
      return next
    })
  }, [])

  const addBlock = useCallback(
    (code: string) => {
      const block: PrettifiedBlock = {id: generateBlockId(), code, collapsed: false}
      updateBlocks(prev => [...prev, block])
    },
    [updateBlocks],
  )

  const removeBlock = useCallback((id: string) => updateBlocks(prev => prev.filter(b => b.id !== id)), [updateBlocks])

  const toggleCollapse = useCallback(
    (id: string) => updateBlocks(prev => prev.map(b => (b.id === id ? {...b, collapsed: !b.collapsed} : b))),
    [updateBlocks],
  )

  const updateBlockCode = useCallback(
    (id: string, newCode: string) => updateBlocks(prev => prev.map(b => (b.id === id ? {...b, code: newCode} : b))),
    [updateBlocks],
  )

  const clearBlocks = useCallback(() => {
    blocksRef.current = []
    setBlocks([])
  }, [])

  /**
   * Returns the content of all prettified blocks as markdown fenced code blocks.
   * Reads from a ref so it is always current even inside async callbacks.
   */
  const getBlocksText = useCallback((): string => {
    return blocksRef.current
      .map(b => {
        // find longest consecutive backtick run in the code and use one longer for the fence
        const maxRun = (b.code.match(/`+/g) ?? []).reduce((max, m) => Math.max(max, m.length), 0)
        const fence = '`'.repeat(Math.max(3, maxRun + 1))
        return `${fence}\n${b.code}\n${fence}`
      })
      .join('\n')
  }, [])

  return {
    blocks,
    addBlock,
    removeBlock,
    updateBlockCode,
    toggleCollapse,
    clearBlocks,
    getBlocksText,
  }
}
